import {Component, inject, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy} from '@angular/core';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {IconDefinition} from '@fortawesome/fontawesome-svg-core';
import {
  faChevronDown,
  faChevronUp,
  faCircleCheck,
  faCircleExclamation,
  faCircleMinus,
  faCircleQuestion,
  faCircleXmark,
  faCopy,
  faFileCircleQuestion,
} from '@fortawesome/pro-solid-svg-icons';
import {BicBadgeComponent, BicButtonComponent, BicToastService} from '@buildinginformationcloud/bic-ui-components';
import {RuleTestModuleTraceEntry, RuleTestRunView} from '../../../../services/rule-test/rule-test.models';
import {formatClockTime, formatDuration} from '../../../../services/rule-test/rule-test.format';
import {
  buildCheckResultRaw,
  CheckResultReportType,
  reportTypeForRulesetType,
} from '../../../../models/report/check-result-raw';
import {getReportAdapter} from '../../../../models/report/check-report-adapter';
import {buildCardModel, CardModel, EMPTY_CARD_MODEL} from '../../../../models/report/report-card-model';
import {
  ReportDisplayConfig,
  resolveReportDisplayConfigFromSnapshot,
} from '../../../../models/report/report-display-config';
import {applyIdsThresholdsToReport, normalizeIdsThresholds} from '../../../../models/report/ids-thresholds';
import {ReportStatus, STATUS_FILTER_LABELS} from '../../../../models/report/report-filter';
import {worstStatus} from '../../../../models/report/report-leaves';
import {NormalizedReport} from '../../../../models/report/normalized-report.model';
import {buildDefaultReportConfigForRuleset} from '../../../my-checks/my-checks-builder/my-checks-builder.model';
import {
  ReportCardViewComponent,
} from '../../../reports/check-report-page/components/report-card-view/report-card-view.component';
import {EmptyStateComponent} from '../../../util/design/empty-state/empty-state.component';

const EMPTY_MESSAGE_AI = 'Der Testlauf hat kein auswertbares Ergebnis geliefert. Die Rohausgabe zeigt, was die KI zurückgegeben hat.';
const EMPTY_MESSAGE_IDS = 'Der Testlauf hat kein auswertbares Ergebnis geliefert. Die Rohausgabe zeigt, was die IDS-Prüfung zurückgegeben hat.';

/** Höchstens so viele Elemente zeigt „Ablauf je Modul", danach „+ n weitere" (Plan §3.3). */
export const MODULE_TRACE_MAX_ELEMENTS = 20;

/** Eine Zeile in „Ablauf je Modul". */
export interface ModuleTraceRow {
  key: string;
  label: string;
  moduleId: string;
  status: RuleTestModuleTraceEntry['status'];
  branch: string | null;
  inputJson: string | null;
  outputJson: string | null;
}

/** Die Zeilen eines Elements (bei positionsweiser Prüfebene), sonst eine einzige Gruppe ohne Titel. */
export interface ModuleTraceGroup {
  key: string;
  element: string | null;
  rows: ModuleTraceRow[];
}

/**
 * Ergebnis eines Testlaufs im Result-Card-Format des Berichts (Figma Frame 04) plus Meta,
 * Rohausgabe und bei Hybrid „Ablauf je Modul".
 *
 * Alles, was an Inputs der Kinder oder an `@for` hängt, wird in `ngOnChanges` EINMAL berechnet und
 * als Feld gehalten (Frontend/AGENTS.md, „Template Bindings & Change Detection") – die Karten-
 * Ansicht ist teuer, ein bei jedem Zyklus neues Modell würde den Tab einfrieren.
 */
@Component({
  selector: 'app-rule-test-result-preview',
  standalone: true,
  imports: [BicBadgeComponent, BicButtonComponent, FaIconComponent, ReportCardViewComponent, EmptyStateComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './rule-test-result-preview.component.html',
})
export class RuleTestResultPreviewComponent implements OnChanges {
  private toastService = inject(BicToastService);

  /** Abgeschlossener Lauf (`status === 'COMPLETED'`). */
  @Input() run: RuleTestRunView | null = null;
  /** Report-Konfiguration aus dem Editor-Stand (`RuleTestPopupContext.reportConfig`). */
  @Input() reportConfig: unknown = null;
  /**
   * Nur IDS: Farbschwellen des Editor-Stands (`RuleTestPopupContext.idsThresholds`) – die Vorschau
   * färbt den Score so, wie es der Bericht mit diesem Stand täte. `null` = binär über den IDS-Status.
   */
  @Input() idsThresholds: unknown = null;

  cardModel: CardModel = EMPTY_CARD_MODEL;
  expandedKeys = new Set<string>();
  /** Ob der Output einen Bericht mit mindestens einer Karte ergab. */
  hasResult = false;
  overallStatus: ReportStatus = 'unknown';
  statusLabel = STATUS_FILTER_LABELS.unknown;
  badgeType: 'info' | 'success' | 'warning' | 'error' = 'info';
  statusIcon: IconDefinition = faCircleQuestion;
  /** „Testlauf 10:42 Uhr · Dauer 0:42 · Claude Sonnet 4.6 · Getestet: ungespeicherte Änderungen". */
  metaText = '';
  /** Leerzustand – nennt, wer die Rohausgabe geliefert hat (KI bzw. IDS-Prüfung). */
  emptyMessage = EMPTY_MESSAGE_AI;

  rawJson = '';
  rawOpen = false;

  traceGroups: ModuleTraceGroup[] = [];
  hiddenTraceElements = 0;
  traceOpen = false;
  expandedTraceRows = new Set<string>();

  protected readonly faChevronDown = faChevronDown;
  protected readonly faChevronUp = faChevronUp;
  protected readonly faCopy = faCopy;
  protected readonly faFileCircleQuestion = faFileCircleQuestion;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['run'] || changes['reportConfig'] || changes['idsThresholds']) {
      this.rebuild();
    }
  }

  toggleCard(key: string): void {
    const next = new Set(this.expandedKeys);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    this.expandedKeys = next;
  }

  toggleTraceRow(key: string): void {
    const next = new Set(this.expandedTraceRows);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    this.expandedTraceRows = next;
  }

  traceStatusIcon(status: ModuleTraceRow['status']): IconDefinition {
    switch (status) {
      case 'COMPLETED':
        return faCircleCheck;
      case 'FAILED':
        return faCircleXmark;
      default:
        return faCircleMinus;
    }
  }

  traceStatusColor(status: ModuleTraceRow['status']): string {
    switch (status) {
      case 'COMPLETED':
        return 'text-green';
      case 'FAILED':
        return 'text-red';
      default:
        return 'text-gray-400';
    }
  }

  copyRaw(): void {
    const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
    if (!clipboard) {
      this.toastService.error('Die Rohausgabe konnte nicht kopiert werden.');
      return;
    }
    clipboard.writeText(this.rawJson).then(
      () => this.toastService.success('Rohausgabe kopiert.'),
      () => this.toastService.error('Die Rohausgabe konnte nicht kopiert werden.'),
    );
  }

  // ─── intern ────────────────────────────────────────────────────────────────────────────────

  private rebuild(): void {
    const run = this.run;
    this.rawJson = run ? stringify(run.output) : '';
    this.metaText = run ? buildMetaText(run) : '';
    this.buildTrace(run);

    const reportType = reportTypeForRulesetType(run?.rulesetType);
    this.emptyMessage = reportType === 'IDS' ? EMPTY_MESSAGE_IDS : EMPTY_MESSAGE_AI;
    const report = run ? parseReport(run, reportType) : null;
    if (report && reportType === 'IDS') {
      const thresholds = normalizeIdsThresholds(this.idsThresholds);
      if (thresholds) applyIdsThresholdsToReport(report, thresholds);
    }
    const model = report
      ? safeCardModel(report, this.resolveConfig(run!, report, reportType), reportType)
      : EMPTY_CARD_MODEL;

    this.cardModel = model;
    this.hasResult = model.itemKeys.length > 0;
    // Erste Karte offen (Frame 04): bei einer Regel an einer Datei ist das die ganze Antwort.
    this.expandedKeys = new Set(model.itemKeys.slice(0, 1));

    this.overallStatus = report ? overallStatusOf(report) : 'unknown';
    this.statusLabel = STATUS_FILTER_LABELS[this.overallStatus];
    this.badgeType = badgeTypeOf(this.overallStatus);
    this.statusIcon = statusIconOf(this.overallStatus);
  }

  /**
   * Reihenfolge: Konfiguration aus dem Editor-Stand → Snapshot im Output → Standard für den Typ.
   *
   * Der Standard bekommt bei der KI-Prüfung die Antwortfelder des Ergebnisses als Detail-Spalten:
   * ohne Ausgabeparameter hätte er gar keine, und die Karte zeigte nur die Schritt-Überschriften.
   */
  private resolveConfig(
    run: RuleTestRunView,
    report: NormalizedReport,
    reportType: CheckResultReportType,
  ): ReportDisplayConfig | null {
    if (isDisplayConfig(this.reportConfig)) {
      return this.reportConfig;
    }
    const snapshot = resolveReportDisplayConfigFromSnapshot(run.output, {checkType: reportType});
    if (snapshot) {
      return snapshot;
    }
    const output = isRecord(run.output) ? run.output : {};
    const pruefebene = typeof output['pruefebene'] === 'string' ? output['pruefebene'] : undefined;
    return buildDefaultReportConfigForRuleset(
      run.rulesetId,
      reportType === 'HYBRID' ? 'HYBRID' : reportType === 'IDS' ? 'IDS' : 'KI',
      pruefebene,
      reportType === 'AI' ? detailFieldNames(report).map(name => ({name})) : undefined,
    );
  }

  private buildTrace(run: RuleTestRunView | null): void {
    this.expandedTraceRows = new Set<string>();
    const entries = run?.rulesetType === 'HYBRID' ? (run.moduleTrace ?? []) : [];
    const grouped = groupModuleTrace(entries);
    this.traceGroups = grouped.groups;
    this.hiddenTraceElements = grouped.hidden;
  }
}

// ─── pure Hilfen (exportiert für die Spec) ─────────────────────────────────────────────────────

/**
 * Gruppiert den Ablauf nach Element in Reihenfolge des ersten Auftretens. Einträge ohne Element
 * bilden eine eigene Gruppe ohne Titel. Gezählt und gekürzt werden nur Element-Gruppen.
 */
export function groupModuleTrace(
  entries: ReadonlyArray<RuleTestModuleTraceEntry>,
  maxElements: number = MODULE_TRACE_MAX_ELEMENTS,
): {groups: ModuleTraceGroup[]; hidden: number} {
  const byElement = new Map<string, ModuleTraceGroup>();
  const order: ModuleTraceGroup[] = [];
  entries.forEach((entry, index) => {
    const element = entry.element != null && String(entry.element).trim() ? String(entry.element) : null;
    const groupKey = element == null ? '__ohne_element__' : `el:${element}`;
    let group = byElement.get(groupKey);
    if (!group) {
      group = {key: groupKey, element, rows: []};
      byElement.set(groupKey, group);
      order.push(group);
    }
    group.rows.push({
      key: `${groupKey}#${index}|${entry.nodeId}`,
      label: entry.label || entry.moduleId || entry.nodeId,
      moduleId: entry.moduleId,
      status: entry.status,
      branch: entry.branch ?? null,
      inputJson: entry.input === undefined || entry.input === null ? null : stringify(entry.input),
      outputJson: entry.output === undefined || entry.output === null ? null : stringify(entry.output),
    });
  });

  const groups: ModuleTraceGroup[] = [];
  let shownElements = 0;
  let hidden = 0;
  for (const group of order) {
    if (group.element == null) {
      groups.push(group);
    } else if (shownElements < maxElements) {
      groups.push(group);
      shownElements++;
    } else {
      hidden++;
    }
  }
  return {groups, hidden};
}

/** Gesamtstatus: bei einer Datei deren Status, sonst der schlechteste über alle Dateien. */
export function overallStatusOf(report: NormalizedReport): ReportStatus {
  const statuses = (report.groups ?? []).map(group => group.status);
  if (statuses.length === 0) return 'unknown';
  return statuses.length === 1 ? statuses[0] : worstStatus(statuses);
}

/** Feldnamen aller Prüfschritte in Reihenfolge des ersten Auftretens. */
export function detailFieldNames(report: NormalizedReport): string[] {
  const names: string[] = [];
  const seen = new Set<string>();
  const visit = (rules: ReadonlyArray<{details?: ReadonlyArray<{fields?: Record<string, unknown> | null}>}>) => {
    for (const rule of rules) {
      for (const detail of rule.details ?? []) {
        for (const key of Object.keys(detail.fields ?? {})) {
          const dedupeKey = key.toLowerCase();
          if (!seen.has(dedupeKey)) {
            seen.add(dedupeKey);
            names.push(key);
          }
        }
      }
    }
  };
  for (const group of report.groups ?? []) {
    visit(group.rules ?? []);
    for (const structure of group.structures ?? []) visit(structure.rules ?? []);
  }
  return names;
}

function parseReport(run: RuleTestRunView, reportType: CheckResultReportType): NormalizedReport | null {
  if (!isRecord(run.output)) return null;
  try {
    const raw = buildCheckResultRaw(run.output, reportType, {
      workflowName: run.ruleLabel,
      checkDate: run.finishedAt ?? run.startedAt,
    });
    return getReportAdapter(reportType).parseReportData(raw);
  } catch {
    // Ein Output, den der Adapter nicht lesen kann, ist ein Leerzustand – die Rohausgabe bleibt.
    return null;
  }
}

function safeCardModel(
  report: NormalizedReport,
  config: ReportDisplayConfig | null,
  reportType: CheckResultReportType,
): CardModel {
  try {
    return buildCardModel(report, config, reportType);
  } catch {
    return EMPTY_CARD_MODEL;
  }
}

function buildMetaText(run: RuleTestRunView): string {
  const parts: string[] = [];
  const time = formatClockTime(run.startedAt);
  if (time) parts.push(`Testlauf ${time}`);
  if (run.durationMs != null) parts.push(`Dauer ${formatDuration(run.durationMs)}`);
  if (run.model) parts.push(run.model);
  // „Getestet: …" statt „Stand: gespeichert": neben „Ergebnis wird nicht gespeichert" las sich das
  // wie ein Widerspruch (gemeldet 2026-10-06). Gemeint ist, WELCHER Stand der Regel lief.
  parts.push(`Getestet: ${run.editorDirty ? 'ungespeicherte Änderungen' : 'gespeicherter Stand'}`);
  return parts.join(' · ');
}

function badgeTypeOf(status: ReportStatus): 'info' | 'success' | 'warning' | 'error' {
  return status === 'unknown' ? 'info' : status;
}

function statusIconOf(status: ReportStatus): IconDefinition {
  switch (status) {
    case 'success':
      return faCircleCheck;
    case 'warning':
      return faCircleExclamation;
    case 'error':
      return faCircleXmark;
    default:
      return faCircleQuestion;
  }
}

function isDisplayConfig(value: unknown): value is ReportDisplayConfig {
  const levels = (value as ReportDisplayConfig | null | undefined)?.levels;
  return !!value && typeof value === 'object' && Array.isArray(levels) && levels.length > 0;
}

function isRecord(value: unknown): value is Record<string, any> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function stringify(value: unknown): string {
  if (value === undefined) return '';
  try {
    return JSON.stringify(value, null, 2) ?? '';
  } catch {
    return String(value);
  }
}
